import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { retryWhen, delay, catchError, take, concatMap, tap } from 'rxjs/operators';

import { UrlService } from '@core/services/url/url.service';
import { NotificationsService } from '@core/services/notifications/notifications.service';

import { File } from '@core/objects/file';
import { NotificationsContextEnum } from '@core/store/models/notifications.enum';

@Injectable({
    providedIn: 'root'
})
export class FilesService {

    private readonly COMPRESSED_URL_SUFFIX = '/files/compressed'
    private readonly ANNOTATIONS_URL_SUFFIX = '/annotations/pdf'
    private readonly ANNOTATIONS_URL_PREFIX = '/files/'
    
    // pdf with annotations url cases
    // /web/app/requests/{rid}/files/{fid}/annotations/pdf
    // /web/app/versions/{vid}/feedbacks/files/{fid}/annotations/pdf
    // /web/share/{type}/{token}/versions/{version_id}/files/{file_id}/annotations/pdf



    constructor(protected urlSrv: UrlService, 
        private notsSrv: NotificationsService, 
        protected http: HttpClient) {}
    /**
     * Download a .zip containing all files related to project or artwork waiting until zip is done to then donwload it directly.
     * @param url url part to set between current baseurl /app or /share and the url to call compressed files 
     * @returns 
     */
    downloadCompressedFile(url: string): Observable<File | Object> {
        const maxRetries = 90;
        const delayTime = 2000; // 2 seconds --> total 180 seconds (3 minutes)
        // build the url
        const compressedUrl = this.urlSrv.build(url + this.COMPRESSED_URL_SUFFIX);
        return this.http.get(compressedUrl).pipe(
            retryWhen(errors =>
                errors.pipe(
                    delay(delayTime), // Delay between retry attempts (adjust as needed)
                    take(maxRetries), // Maximum number of retry attempts (adjust as needed)
                    concatMap((error, retryCount) => {
                        if (error.status === 425) {
                            return timer(0); // Retry immediately
                        } else if (error.status === 404) { // manage errors wiht notifications.
                            const notDetailId = Math.random().toString(36).replace('0.', '');
                            let details = { message: 'The file not exists' ,title: "Preparing Files"}
                            this.notsSrv.addNotification(NotificationsContextEnum.EVENT_FAILED, {id: notDetailId, details: details});
                            return throwError(() => error); // add notification failed and retrn error
                        } else {
                            const notDetailId = Math.random().toString(36).replace('0.', '');
                            let details = { message: 'There was an error preparing your file.' ,title: "Preparing Files Process"}
                            this.notsSrv.addNotification(NotificationsContextEnum.EVENT_FAILED, {id: notDetailId, details: details});
                            return throwError(() => error); // Throw error for non-425 status codes
                        }
                    }),
                    catchError(error => throwError(() => error))
                )
            ),
            // DON'T DELETE: tap operator allows you to perform side effects, such as triggering notifications, 
            // without modifying the emitted values returned to the subscriber.
            // tap(compressedFile => {
            //     if (compressedFile) {
                    // TODO Use to show some success notification and maybe download the file there
                    // this.notSrv.addNotification( .... ); // Trigger the notification service to show success
                // }
            // })
        );      
    }

    /**
     * Check the download PDF containing all annotations associtaed with waiting until PDF is done to then donwload it directly on the notification.
     * @param url url part to set between current baseurl /app or /share and the url to call the donwload file with annotiations 
     * @returns 
     */
    getDownloadPdfAnnotationsFile(file: File, url: string): Observable<File | Object> {
        const maxRetries = 90;
        const delayTime = 2000; // 2 seconds --> total 180 seconds (3 minutes)
        // build the url
        const annotationsUrl = this.urlSrv.build(url + this.ANNOTATIONS_URL_PREFIX + file.id + this.ANNOTATIONS_URL_SUFFIX).replace('/p/','/'); // quit /p for plugins url www.youtube.com/watch?v=90GqAf3zJ8s&t=65s

        const headers = new HttpHeaders();
        return this.http.get(annotationsUrl, { headers }).pipe( // adding  observe: 'response' we send full response, not only the body
            retryWhen(errors =>
                errors.pipe(
                    delay(delayTime), // Delay between retry attempts (adjust as needed)
                    take(maxRetries), // Maximum number of retry attempts (adjust as needed)
                    concatMap((error, retryCount) => {
                        if (error.status === 425) {
                            return timer(0); // Retry immediately
                        } else if (error.status === 404) { // manage errors wiht notifications.
                            console.debug(" errorrrrr ",error.erorr)
                            const notDetailId = Math.random().toString(36).replace('0.', '');
                            let details; 
                            // check code to manage the notification error message
                            if(error.error?.code){
                                switch (error.error.code) {
                                    case 'not_found_http_exception':
                                        details = { message: 'There was a error creating the file, please contact your admin for support.' ,title: "Preparing File Process"};
                                        break;
                                    case 'xfdf_annotations_in_file_not_found':
                                    default:
                                        details = { message: 'The file with the annotations could not be created.<br/>Try to open the file preview to review them and try again.' ,title: "Preparing File"};
                                        break;
                                }
                            }
                            this.notsSrv.addNotification(NotificationsContextEnum.EVENT_FAILED, {id: notDetailId, details: details});
                            return throwError(() => error); // add notification failed and retrn error
                        } else {
                            const notDetailId = Math.random().toString(36).replace('0.', '');
                            let details = { message: 'There was an error preparing your file.' ,title: "Preparing File Process"}
                            this.notsSrv.addNotification(NotificationsContextEnum.EVENT_FAILED, {id: notDetailId, details: details});
                            return throwError(() => error); // Throw error for non-425 status codes
                        }
                    }),
                    catchError(error => throwError(() => error))
                )
            ),
            // This was when it is complete and we not return an id.
            // tap(compressedFile => {
            //     if (compressedFile) {
            //         console.debug(" Ending the donwload process. ")
            //         window.location.href =  annotationsUrl;  // trigger the download directly when finished
            //         // TODO maybe show some success notification
            //         // this.notSrv.addNotification( .... ); // Trigger the notification service to show success
            //     }
            // })
        )
    }
}
